import numeral from "numeral";
import { STATE_ABBR } from "utils/stateAbbreviations";

export const pluralize = (noun, count, suffix = "s") =>
  `${noun}${Number(count) !== 1 ? suffix : ""}`;

/**
 * @param {string} _count
 * @param {boolean} hideLowCount
 * @param {number} lowCountLimit
 * @param {string} defaultFormatter
 * @returns {string}
 * @description Format count to compact format. E.g. based on default value:
 * Less than lowCountLimit, show count
 * (count > 9999 AND count < 100000) or (count > 9999999 AND count < 100000000), show 10.0k or 10.0m
 * (count > 99999 AND count < 1000000) or (count > 99999999 AND count < 1000000000), show 100k, 100m
 * The rest would be, e.g.
 * 3000 - 3.0k
 * 3100 - 3.1k
 * 3123 - 3.1k
 * 3951 - 3.9k
 * ...
 * */
export const formatCount = (
  _count,
  hideLowCount = false,
  lowCountLimit = 1000,
  defaultFormatter = "0.0a"
) => {
  const count = Number(_count || 0);
  let formatter = defaultFormatter;

  if (!hideLowCount && count < lowCountLimit) {
    return _count;
  }

  if (hideLowCount && count < lowCountLimit) {
    return "";
  }

  if (
    (count > 9999 && count < 100000) ||
    (count > 9999999 && count < 100000000)
  ) {
    formatter = "0.0a";
  }

  if (
    (count > 99999 && count < 1000000) ||
    (count > 99999999 && count < 1000000000)
  ) {
    formatter = "0a";
  }

  return numeral(count).format(formatter, Math.floor).replace(".00", "");
}

export const formatLowCount = (_count, ignoreMinCount = false) => {
  const count = Number(_count || 0);

  if (count >= 1000) {
    return formatCount(_count);
  }
  if (count >= 500) {
    return "500+";
  }
  if (count >= 400) {
    return "400+";
  }
  if (count >= 300) {
    return "300+";
  }
  if (count >= 200) {
    return "200+";
  }
  if (count >= 100) {
    return "100+";
  }
  if (count >= 50) {
    return "50+";
  }

  if (ignoreMinCount) {
    return _count;
  }
  return null;
}

export const formatCityLabel = (city, state, country) => {
  if (!city) {
    return "";
  }

  if (country === "US" || country === "United States") {
    const stateAbbr = state?.length === 2 ? state : STATE_ABBR[state];
    return `${city}, ${stateAbbr}`;
  }
  return `${city}, ${country}`;
}

/**
 * @param {Date} date
 * @returns {number} unix timestamp in seconds
 */
export const formatTimestamp = (date) => {
  return Math.floor(date.getTime() / 1000);
}

/**
 * @param {Date} date
 * @param {string} offset - "start" or "end"
 * @returns {Date}
 * */
export const formatDateTimestamp = (date, offset) => {
  return offset === "end"
    ? new Date(date.setHours(23, 59, 59, 999))
    : new Date(date.setHours(0, 0, 0, 0));
}
